import { useEffect, useLayoutEffect, useState, useMemo, useRef, createElement, isValidElement } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { MapPin, Route, Ticket ,CalendarDays, Search} from "lucide-react";
import { trainsData, classOptions, trainTypeOptions, timeSlots } from "./Train";
import "./Train.css";
import usePageMeta from "../../../hooks/usePageMeta";



// ── Helpers ────────────────────────────────────────────────────────────────
const today = new Date("2026-05-07");
function getDateStrip() {
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(today);
    d.setDate(today.getDate() - 1 + i);
    return {
      day: d.toLocaleDateString("en-IN", { weekday: "short" }),
      date: d.getDate(),
      month: d.toLocaleDateString("en-IN", { month: "short" }),
      dateObj: d,
    };
  });
}
const dateStrip = getDateStrip();

const isSameDay = (a, b) => {
  if (!a || !b) return false;
  return new Date(a).toDateString() === new Date(b).toDateString();
};

const buildMonthGrid = (baseDate) => {
  const monthStart = new Date(baseDate.getFullYear(), baseDate.getMonth(), 1);
  const weekStart = new Date(monthStart);
  weekStart.setDate(monthStart.getDate() - monthStart.getDay());
  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(weekStart);
    date.setDate(weekStart.getDate() + index);
    return date;
  });
};

const formatIsoDate = (iso) => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatDateParts = (iso) => {
  const date = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(date.getTime())) return { day: "--", monthYear: "", weekday: "" };
  return {
    day: date.getDate(),
    monthYear: date
      .toLocaleDateString("en-IN", { month: "short", year: "2-digit" })
      .replace(" ", "'"),
    weekday: date.toLocaleDateString("en-IN", { weekday: "long" }),
  };
};

const sortOptions = [
  { value: "departure", label: "Departure" },
  { value: "arrival", label: "Arrival" },
  { value: "duration", label: "Duration" },
  { value: "price-asc", label: "Price ↑" },
  { value: "price-desc", label: "Price ↓" },
];

const classBgMap = {
  SL: "#e0f2fe", "2S": "#fef9c3", CC: "#dcfce7",
  "3A": "#ede9fe", "2A": "#dbeafe", "1A": "#fce7f3",
  EC: "#ffedd5", "3E": "#d1fae5",
};
const classColorMap = {
  SL: "#0369a1", "2S": "#854d0e", CC: "#166534",
  "3A": "#6d28d9", "2A": "#1d4ed8", "1A": "#be185d",
  EC: "#c2410c", "3E": "#065f46",
};

// Icon per class
const classIconMap = {
  SL: "🛏️", "2S": "💺", CC: "💺",
  "3A": "🛌", "2A": "🛌", "1A": "🛌",
  EC: "💺", "3E": "🛏️",
};

function ClassChip({ code }) {
  return (
    <span className="class-chip" style={{ background: classBgMap[code] || "#f1f5f9", color: classColorMap[code] || "#475569" }}>
      {code}
    </span>
  );
}

const isToday = (iso) => {
  const t = new Date().toISOString().slice(0, 10);
  return iso === t;
};

const normalizeRoute = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/\s*\([^)]*\)/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const renderIconNode = (icon, props = {}) => {
  if (!icon) return null;
  if (isValidElement(icon)) return icon;
  if (typeof icon === "function" || typeof icon === "object") return createElement(icon, props);
  return icon;
};

function CheckboxFilter({ label, options, selected, onChange, counts }) {
  const toggle = (v) => onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  return (
    <div className="tp-filter-group">
      <h4 className="tp-filter-label">{label}</h4>
      {options.map(({ code, label: lbl }) => (
        <label key={code} className="tp-checkbox-row">
          <input type="checkbox" checked={selected.includes(code)} onChange={() => toggle(code)} />
          <span className="tp-custom-check" />
          <span className="tp-check-label">{lbl}</span>
          {counts && counts[code] !== undefined && (
            <span className="tp-count">{counts[code]}</span>
          )}
        </label>
      ))}
    </div>
  );
}

function TimeSlotGrid({ label, selected, onChange }) {
  const toggle = (v) => onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  return (
    <div className="tp-filter-group">
      <h4 className="tp-filter-label">{label}</h4>
      <div className="tp-slot-grid">
        {timeSlots.map(({ value, label: lbl, icon }) => (
          <button key={value} className={`tp-slot ${selected.includes(value) ? "tp-slot-active" : ""}`} onClick={() => toggle(value)}>
            <span className="tp-slot-icon">{renderIconNode(icon, { size: 16, strokeWidth: 2 })}</span>
            <span>{lbl}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Date Popup for each card ────────────────────────────────────────────────
function DatePopup({ trainId, selectedDate, setSelectedDate, onClose, anchorRef }) {
  const popupRef = useRef(null);
  const [coords, setCoords] = useState({ top: 0, left: 0 });

  useLayoutEffect(() => {
    if (!anchorRef.current) return;
    const updateCoords = () => {
      const rect = anchorRef.current.getBoundingClientRect();
      setCoords({ top: rect.bottom + 8, left: rect.left });
    };
    updateCoords();
    window.addEventListener("resize", updateCoords);
    window.addEventListener("scroll", updateCoords, true);
    return () => {
      window.removeEventListener("resize", updateCoords);
      window.removeEventListener("scroll", updateCoords, true);
    };
  }, [anchorRef]);

  useEffect(() => {
    const handleClick = (e) => {
      const insideAnchor = anchorRef.current && anchorRef.current.contains(e.target);
      const insidePopup = popupRef.current && popupRef.current.contains(e.target);
      if (!insideAnchor && !insidePopup) onClose();
    };
    const handleKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [onClose, anchorRef]);

  return (
    <div
      className="tp-date-popup"
      ref={popupRef}
      style={{ position: "fixed", top: coords.top, left: coords.left }}
    >
      <div className="tp-date-popup-header">
        <span>🗓️ Choose Journey Date</span>
        <button className="tp-date-popup-close" onClick={onClose}>✕</button>
      </div>
      <div className="tp-nearby-dates">
        {dateStrip.map((d, i) => (
          <button
            key={i}
            className={`tp-nearby-date ${i === selectedDate ? "tp-nd-active" : ""}`}
            onClick={() => { setSelectedDate(i); onClose(); }}
          >
            <span>{d.day}</span>
            <span>{d.date} {d.month}</span>
          </button>
        ))}
      </div>
      <div className="tp-date-popup-footer">
        <span>✓ Secure Booking</span>
        <span>✓ 100% Verified</span>
        <span>✓ IRCTC Authorised</span>
      </div>
    </div>
  );
}

// ── Generate booking date cards around a base date ─────────────────────────
function getBookingDates(baseDate, cls) {
  // Simulate 6 nearby dates with price + availability
  const availStates = [
    { type: "RAC",       label: (n) => `RAC ${n}`,          color: "red"   },
    { type: "AVAILABLE", label: (n) => `AVAILABLE-${n}`,    color: "green" },
    { type: "WL",        label: (n) => `WL${n}`,            color: "red"   },
    { type: "RAC",       label: (n) => `RAC ${n}`,          color: "red"   },
    { type: "AVAILABLE", label: (n) => `AVAILABLE-${n}`,    color: "green" },
    { type: "AVAILABLE", label: (n) => `AVAILABLE-${n}`,    color: "green" },
  ];
  const seeds = [23, 46, 5, 35, 81, 153];
  const timeAgoOptions = ["Just now", "about an hour ago", "2 hours ago", "3 days ago", "2 days ago", "5 hours ago"];
  return Array.from({ length: 6 }, (_, i) => {
    const d = new Date(baseDate);
    d.setDate(baseDate.getDate() + i);
    const av = availStates[i];
    return {
      dateObj: d,
      label: `${d.toLocaleDateString("en-IN", { month: "short", day: "numeric" })}, ${d.toLocaleDateString("en-IN", { weekday: "short" })}`,
      weekday: d.toLocaleDateString("en-IN", { weekday: "short" }),
      price: cls.price,
      avail: av.label(seeds[i]),
      availColor: av.color,
      updatedAgo: timeAgoOptions[i],
    };
  });
}

// ── Booking Modal — centered popup shown on "Book →" click ─────────────────
function BookingDropdown({ train, cls, baseDate, onClose }) {
  // defensive (in case props mismatch)
  const safeTrain = train || {};
  const fromCity = safeTrain.departureCity || "";
  const toCity = safeTrain.arrivalCity || "";

  const dates = useMemo(() => getBookingDates(baseDate, cls), [baseDate, cls]);



  useEffect(() => {
    const handleKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  return createPortal(
    <div className="tp-booking-modal-overlay" onClick={onClose}>
      <div className="tp-booking-drop" onClick={(e) => e.stopPropagation()}>
        <div className="tp-booking-drop-header">
          <span>Select Journey Date — <strong>{cls.code}</strong> class</span>
          <button className="tp-date-popup-close" onClick={onClose}>✕</button>
        </div>
        <div className="tp-booking-drop-dates">
          {dates.map((d, i) => (
            <div key={i} className={`tp-booking-date-card tp-bdc-tint-${d.availColor}`}>
              <div className="tp-bdc-date">{d.label}</div>
              <div className="tp-bdc-price-center">₹{d.price.toLocaleString()}</div>
              <div className={`tp-bdc-avail tp-bdc-avail-${d.availColor}`}>{d.avail}</div>
              <button
                className="tp-bdc-book-btn"
                type="button"
                onClick={() => {
                  const dateIso = baseDate.toISOString().slice(0, 10);
                  const params = new URLSearchParams({
                    from: fromCity,
                    to: toCity,
                    date: dateIso,
                    class: cls.code,
                  });

                  window.open(`/trainbooking/${train.id}?${params.toString()}`, "_blank", "noopener,noreferrer");
                  onClose();
                }}
              >
                Book Now
              </button>

            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── Train Card ─────────────────────────────────────────────────────────────
function TrainCard({ train, idx, selectedDate, setSelectedDate, expandedId, setExpandedId }) {
  const [datePopupOpen, setDatePopupOpen] = useState(false);
  const dateButtonRef = useRef(null);
  // Track which class has its booking modal open: stores class index or null
  const [bookingDropClass, setBookingDropClass] = useState(null);
  const daysOfWeek = ["S", "M", "T", "W", "T", "F", "S"];
  const isExpanded = expandedId === train.id;

  return (
    <div
      className={`tp-train-card ${isExpanded ? "tp-expanded" : ""}`}
      style={{ animationDelay: `${idx * 0.04}s` }}
    >
      {/* ── Main card row ── */}
      <div className="tp-card-main" onClick={() => setExpandedId(isExpanded ? null : train.id)}>

        {/* Left: train icon + name + meta + runs-on */}
        <div className="tp-train-info">
          <div className="tp-train-icon-badge">
            <span className="tp-train-rail-icon">🚂</span>
          </div>
          <div className="tp-train-text">
            <div className="tp-train-name">{train.name}</div>
            <div className="tp-train-meta">
              <span className="tp-train-num">#{train.number}</span>
              <span className={`tp-type-badge tp-type-${train.typeCode.toLowerCase()}`}>{train.typeCode}</span>
            </div>
          </div>
          {/* <div className="tp-card-chevron">›</div> */}
        </div>

        {/* Center: departure → duration → arrival */}
        <div className="tp-journey-row">
          <div className="tp-time-block">
            <div className="tp-time">{train.departure}</div>
            <div className="tp-city">{train.departureCity}</div>
          </div>

          <div className="tp-duration-block">
            <span className="tp-dur-label">{train.duration}</span>
            <div className="tp-dur-line">
              <div className="tp-dur-dot left" />
              <div className="tp-dur-track">
                <div className="tp-dur-fill" />
                <span className="tp-train-loco">🚆</span>
              </div>
              <div className="tp-dur-dot right" />
            </div>
            <span className="tp-nonstop-badge">Non Stop</span>
          </div>

          <div className="tp-time-block tp-text-right">
            <div className="tp-time">{train.arrival}</div>
            <div className="tp-city">{train.arrivalCity}</div>
          </div>
        </div>

        {/* Runs-on days */}
        {/* <div className="tp-runs-on-row">
          {daysOfWeek.map((d, i) => (
            <span key={i} className={`tp-day ${train.runsOn.includes(d) ? "tp-day-active" : ""}`}>{d}</span>
          ))}
        </div> */}
      </div>

      {/* ── Class chips row (bottom) ── */}
      <div className="tp-classes-footer" onClick={(e) => e.stopPropagation()}>
        <div className="tp-classes-grid">
          {train.classes.slice(0, 3).map((cls, i) => (
            <div key={i} className="tp-class-box" style={{ "--accent": classColorMap[cls.code] || "#64748b" }}>
              {/* Row 1: class badge */}
              <ClassChip code={cls.code} />
              {/* Row 2: icon + price */}
              <div className="tp-class-price-row">
                <span className="tp-class-icon">{classIconMap[cls.code] || "🪑"}</span>
                <span className="tp-class-price-inline">₹ {cls.price.toLocaleString()}</span>
              </div>
              {/* Row 3: availability */}
              {cls.available !== null ? (
                <span className={`tp-avail-inline ${cls.available <= 10 ? "tp-avail-low" : "tp-avail-ok"}`}>
                  AVL {cls.available}
                </span>
              ) : (
                <span className="tp-avail-inline tp-avail-wl">{cls.waitlist || "WL"}</span>
              )}
              {/* Row 4: book button */}
              <button
                className="tp-book-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setBookingDropClass((prev) => (prev === i ? null : i));
                }}
              >
                Book →
              </button>
              {/* Centered booking modal for this class */}
              {bookingDropClass === i && (
  <BookingDropdown
    train={train}
    cls={cls}
    baseDate={dateStrip[selectedDate]?.dateObj || today}
    onClose={() => setBookingDropClass(null)}
  />
)}
            </div>
          ))}
        </div>

        {/* Choose Journey Date button → popup */}
        <div className="tp-date-btn-wrap">
          {/* <button
            ref={dateButtonRef}
            className="tp-nearby-btn"
            onClick={(e) => { e.stopPropagation(); setDatePopupOpen((p) => !p); }}
          >
            🗓️ {dateStrip[selectedDate]?.date} {dateStrip[selectedDate]?.month}
          </button> */}
          {datePopupOpen && (
            <DatePopup
              trainId={train.id}
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              onClose={() => setDatePopupOpen(false)}
              anchorRef={dateButtonRef}
            />
          )}
        </div>
      </div>

      {/* ── Expanded detail ── */}
      {/* {isExpanded && (
        <div className="tp-expanded-detail">
          <div className="tp-card-benefits">
            <span>Secure Booking</span>
            <span>100% Verified</span>
            <span>IRCTC Authorised Partner</span>
          </div>
        </div> */}
      {/* )} */}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────
export default function TrainsPage() {
  usePageMeta(
  "Book Train Tickets Online | Check Trains & Classes – MakeBeeTrip",
  "Find trains between stations, compare classes and timings, and book train tickets online on MakeBeeTrip. A simple way to plan your next rail journey in India.",
  "train booking, train tickets online, train search, rail tickets, train classes, indian railways booking, train time table"
);
  const navigate = useNavigate();
  const location = useLocation();
  const [from, setFrom] = useState("New Delhi");
  const [to, setTo] = useState("Kanpur Central");
  const [selectedDate, setSelectedDate] = useState(1);
  const [selectedDateValue, setSelectedDateValue] = useState(dateStrip[1].dateObj.toISOString().slice(0, 10));
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarViewMonth, setCalendarViewMonth] = useState(dateStrip[1].dateObj);
  const calendarRef = useRef(null);
  const calendarPopoverRef = useRef(null);
  const [calendarCoords, setCalendarCoords] = useState({ top: 0, left: 0, width: 300 });
  const [sortBy, setSortBy] = useState("departure");
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  // Filters
  const [filterClasses, setFilterClasses] = useState([]);
  const [filterTrainTypes, setFilterTrainTypes] = useState([]);
  const [filterDeparture, setFilterDeparture] = useState([]);
  const [filterArrival, setFilterArrival] = useState([]);
  const [filterTatkal, setFilterTatkal] = useState(false);
  const [filterAvailableOnly, setFilterAvailableOnly] = useState(false);
  const [filterMaxDuration, setFilterMaxDuration] = useState(480);
  const [filterMaxPrice, setFilterMaxPrice] = useState(3000);
  const [filterRunsToday, setFilterRunsToday] = useState(false);
  const [filterQuota, setFilterQuota] = useState([]);

  const swapCities = () => { setFrom(to); setTo(from); };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const fromParam = params.get("from");
    const toParam = params.get("to");
    const dateParam = params.get("date");
    const quotaParam = params.get("quota");

    if (fromParam) setFrom(fromParam);
    if (toParam) setTo(toParam);
    if (quotaParam) setFilterQuota([quotaParam]);

    if (dateParam) {
      const target = new Date(`${dateParam}T00:00:00`);
      if (!Number.isNaN(target.getTime())) {
        setSelectedDateValue(target.toISOString().slice(0, 10));
        setCalendarViewMonth(target);
        for (let i = 0; i < dateStrip.length; i++) {
          const dt = new Date(today);
          dt.setDate(today.getDate() - 1 + i);
          if (dt.toDateString() === target.toDateString()) {
            setSelectedDate(i);
            break;
          }
        }
      }
    }
  }, [location.search]);

  useEffect(() => {
    if (!calendarOpen) return;
    const handleClick = (e) => {
      const insideTrigger = calendarRef.current && calendarRef.current.contains(e.target);
      const insidePopover = calendarPopoverRef.current && calendarPopoverRef.current.contains(e.target);
      if (!insideTrigger && !insidePopover) setCalendarOpen(false);
    };
    const handleKey = (e) => { if (e.key === "Escape") setCalendarOpen(false); };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [calendarOpen]);

  useLayoutEffect(() => {
    if (!calendarOpen || !calendarRef.current) return;
    const updateCoords = () => {
      const rect = calendarRef.current.getBoundingClientRect();
      const popoverWidth = Math.max(rect.width, 300);
      let left = rect.left;
      const maxLeft = window.innerWidth - popoverWidth - 12;
      if (left > maxLeft) left = Math.max(12, maxLeft);
      setCalendarCoords({ top: rect.bottom + 10, left, width: popoverWidth });
    };
    updateCoords();
    window.addEventListener("resize", updateCoords);
    window.addEventListener("scroll", updateCoords, true);
    return () => {
      window.removeEventListener("resize", updateCoords);
      window.removeEventListener("scroll", updateCoords, true);
    };
  }, [calendarOpen, calendarViewMonth]);

  const handleSearch = () => {
    const params = new URLSearchParams({
      from,
      to,
      date: dateStrip[selectedDate].dateObj.toISOString().slice(0, 10),
      quota: filterQuota[0] || "General",
    });
    navigate(`/trains?${params.toString()}`);
  };

  const clearAll = () => {
    setFilterClasses([]);
    setFilterTrainTypes([]);
    setFilterDeparture([]);
    setFilterArrival([]);
    setFilterTatkal(false);
    setFilterAvailableOnly(false);
    setFilterMaxDuration(480);
    setFilterMaxPrice(3000);
    setFilterRunsToday(false);
    setFilterQuota([]);
    setSortBy("departure");
  };

  const activeFilterCount = [
    filterClasses.length, filterTrainTypes.length,
    filterDeparture.length, filterArrival.length,
    filterTatkal ? 1 : 0, filterAvailableOnly ? 1 : 0,
    filterMaxDuration < 480 ? 1 : 0, filterMaxPrice < 3000 ? 1 : 0,
    filterRunsToday ? 1 : 0, filterQuota.length,
  ].reduce((a, b) => a + b, 0);

  const todayDay = ["S", "M", "T", "W", "T", "F", "S"][today.getDay()];

  const getMinPrice = (train) => Math.min(...train.classes.map((c) => c.price));

  const filtered = useMemo(() => {
    let list = [...trainsData];

    const routeFrom = normalizeRoute(from);
    const routeTo = normalizeRoute(to);
    if (routeFrom)
      list = list.filter((t) => normalizeRoute(t.departureCity).includes(routeFrom));
    if (routeTo)
      list = list.filter((t) => normalizeRoute(t.arrivalCity).includes(routeTo));

    if (filterClasses.length)
      list = list.filter((t) => filterClasses.some((cls) => t.classes.some((c) => c.code === cls)));
    if (filterTrainTypes.length)
      list = list.filter((t) => filterTrainTypes.includes(t.typeCode));
    if (filterDeparture.length)
      list = list.filter((t) => filterDeparture.includes(t.departureSlot));
    if (filterArrival.length)
      list = list.filter((t) => filterArrival.includes(t.arrivalSlot));
    if (filterTatkal)
      list = list.filter((t) => t.isTatkal);
    if (filterAvailableOnly)
      list = list.filter((t) => t.classes.some((c) => c.available && c.available > 0));
    if (filterMaxDuration < 480)
      list = list.filter((t) => t.durationMins <= filterMaxDuration);
    if (filterMaxPrice < 3000)
      list = list.filter((t) => getMinPrice(t) <= filterMaxPrice);
    if (filterRunsToday)
      list = list.filter((t) => t.runsOn.includes(todayDay));
    if (filterQuota.includes("general"))
      list = list.filter((t) => t.classes.some((c) => !c.isTatkal));
    if (filterQuota.includes("tatkal"))
      list = list.filter((t) => t.classes.some((c) => c.isTatkal));
    if (filterQuota.includes("premium"))
      list = list.filter((t) => t.isPremiumTatkal);

    switch (sortBy) {
      case "arrival": list.sort((a, b) => a.arrivalTime24 - b.arrivalTime24); break;
      case "duration": list.sort((a, b) => a.durationMins - b.durationMins); break;
      case "price-asc": list.sort((a, b) => getMinPrice(a) - getMinPrice(b)); break;
      case "price-desc": list.sort((a, b) => getMinPrice(b) - getMinPrice(a)); break;
      default: list.sort((a, b) => a.departureTime24 - b.departureTime24);
    }
    return list;
  }, [filterClasses, filterTrainTypes, filterDeparture, filterArrival,
    filterTatkal, filterAvailableOnly, filterMaxDuration, filterMaxPrice,
    filterRunsToday, filterQuota, sortBy]);

  const trainTypeCounts = useMemo(() => {
    const counts = {};
    trainsData.forEach((t) => { counts[t.typeCode] = (counts[t.typeCode] || 0) + 1; });
    return counts;
  }, []);

  const classCounts = useMemo(() => {
    const counts = {};
    trainsData.forEach((t) => t.classes.forEach((c) => { counts[c.code] = (counts[c.code] || 0) + 1; }));
    return counts;
  }, []);

  return (
    <div className="tp-root">
      {/* ── Header ── */}
      <header className="tp-header">
        <div className="tp-searchbar">
          <div className="tp-search-field">
            <span className="tp-field-tag">FROM</span>
            <span className="tp-search-icon tp-icon-from" aria-hidden>
              <MapPin size={18} />
            </span>
            <input value={from} onChange={(e) => setFrom(e.target.value)} placeholder="" />
          </div>
          <button className="tp-swap-btn" onClick={swapCities} title="Swap">⇄</button>
          <div className="tp-search-field">
            <span className="tp-field-tag">TO</span>
            <span className="tp-search-icon tp-icon-to" aria-hidden>
              <Route size={18} />
            </span>
            <input value={to} onChange={(e) => setTo(e.target.value)} placeholder="" />
          </div>


          {/* Date field with calendar popup */}
          <div className="tp-search-field tp-date-field" ref={calendarRef} style={{ position: "relative" }}>
            <button
              type="button"
              className="tp-date-toggle"
              onClick={() => setCalendarOpen((prev) => !prev)}
            >
              <div className="tp-date-toggle-top">
                <span className="tp-field-tag">DEPART</span>
                <span className={`tp-date-chevron ${calendarOpen ? "open" : ""}`}>⌄</span>
              </div>
              <div className="tp-date-toggle-main">
                <span className="tp-date-icon">
  <CalendarDays size={16} />
</span>
                <div className="tp-date-main-text">
                  <div className="tp-date-num-row">
                    <span className="tp-date-num">{formatDateParts(selectedDateValue).day}</span>
                    <span className="tp-date-monthyear">{formatDateParts(selectedDateValue).monthYear}</span>
                  </div>
                  <div className="tp-date-weekday">{formatDateParts(selectedDateValue).weekday}</div>
                </div>
              </div>
            </button>

            {calendarOpen && (
              <div
                className="tp-calendar-popover"
                ref={calendarPopoverRef}
                style={{
                  position: "fixed",
                  top: calendarCoords.top,
                  left: calendarCoords.left,
                  width: calendarCoords.width,
                }}
              >
                <div className="tp-calendar-header">
                  <button
                    type="button"
                    className="tp-calendar-nav-btn"
                    onClick={() => setCalendarViewMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))}
                  >‹</button>
                  <div className="tp-calendar-title">
                    <div className="tp-calendar-month-year">
                      {calendarViewMonth.toLocaleDateString("en-IN", { month: "long", year: "numeric" })}
                    </div>
                    <div className="tp-calendar-subtitle">Select a date</div>
                  </div>
                  <button
                    type="button"
                    className="tp-calendar-nav-btn"
                    onClick={() => setCalendarViewMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))}
                  >›</button>
                </div>

                <div className="tp-calendar-weekdays">
                  {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => <div key={i}>{d}</div>)}
                </div>

                <div className="tp-calendar-grid">
                  {buildMonthGrid(calendarViewMonth).map((date, index) => {
                    const iso = date.toISOString().slice(0, 10);
                    const isCurrentMonth = date.getMonth() === calendarViewMonth.getMonth();
                    const isSelected = iso === selectedDateValue;
                    const isTodayDate = isToday(iso);
                    return (
                      <button
                        key={index}
                        type="button"
                        disabled={!isCurrentMonth}
                        className={`tp-calendar-day ${isSelected ? "selected" : ""} ${!isCurrentMonth ? "muted" : ""} ${isTodayDate && !isSelected ? "today" : ""}`}
                        onClick={() => {
                          setSelectedDateValue(iso);
                          setCalendarOpen(false);
                          const foundIndex = dateStrip.findIndex((item) => item.dateObj.toISOString().slice(0, 10) === iso);
                          setSelectedDate(foundIndex >= 0 ? foundIndex : -1);
                        }}
                      >
                        {date.getDate()}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="tp-search-field tp-class-field">
            <span className="tp-field-tag">CLASS</span>
            <span className="tp-search-icon tp-icon-class" aria-hidden>
              <Ticket size={18} />
            </span>
            <select className="tp-class-select">
              <option>All Classes</option>
              {classOptions.map((c) => <option key={c.code}>{c.label}</option>)}
            </select>
          </div>
<button className="tp-search-btn" onClick={handleSearch}>
  <Search size={15} className="tp-icon-search-btn" style={{ marginRight: 6, verticalAlign: -2 }} />
  Search Trains
</button>
        </div>
      </header>

      {/* ── Date Strip ── */}
      <div className="tp-date-strip">
        <button className="tp-strip-nav">‹</button>
        <div className="tp-strip-dates">
          {dateStrip.map((d, i) => (
            <button
              key={i}
              className={`tp-strip-date ${i === selectedDate ? "tp-date-active" : ""}`}
              onClick={() => setSelectedDate(i)}
            >
              <span className="tp-sd-day">{d.day}</span>
              <span className="tp-sd-num">{d.date} {d.month}</span>
            </button>
          ))}
        </div>
        <button className="tp-strip-nav">›</button>
      </div>

      <div className="tp-body">
        {/* ── Sidebar ── */}
        <aside className={`tp-sidebar ${filtersOpen ? "open" : "closed"}`}>
          <div className="tp-sidebar-head">
            <h3>Filters {activeFilterCount > 0 && <span className="tp-filter-badge">{activeFilterCount}</span>}</h3>
            <button className="tp-clear-btn" onClick={clearAll}>Reset</button>
          </div>

          <div className="tp-filter-group">
            <label className="tp-toggle-row">
              <span>⚡ Tatkal Only</span>
              <span className="tp-toggle-wrap">
                <input type="checkbox" className="tp-toggle-input" checked={filterTatkal} onChange={(e) => setFilterTatkal(e.target.checked)} />
                <span className="tp-toggle-track"><span className="tp-toggle-thumb" /></span>
              </span>
            </label>
            <label className="tp-toggle-row" style={{ marginTop: 10 }}>
              <span>✅ Available Only</span>
              <span className="tp-toggle-wrap">
                <input type="checkbox" className="tp-toggle-input" checked={filterAvailableOnly} onChange={(e) => setFilterAvailableOnly(e.target.checked)} />
                <span className="tp-toggle-track"><span className="tp-toggle-thumb" /></span>
              </span>
            </label>
            <label className="tp-toggle-row" style={{ marginTop: 10 }}>
              <span>📅 Runs Today</span>
              <span className="tp-toggle-wrap">
                <input type="checkbox" className="tp-toggle-input" checked={filterRunsToday} onChange={(e) => setFilterRunsToday(e.target.checked)} />
                <span className="tp-toggle-track"><span className="tp-toggle-thumb" /></span>
              </span>
            </label>
          </div>

          <CheckboxFilter
            label="Booking Quota"
            options={[
              { code: "general", label: "General Quota" },
              { code: "tatkal", label: "Tatkal Quota" },
              { code: "premium", label: "Premium Tatkal" },
            ]}
            selected={filterQuota}
            onChange={setFilterQuota}
          />

          <CheckboxFilter
            label="Travel Class"
            options={classOptions}
            selected={filterClasses}
            onChange={setFilterClasses}
            counts={classCounts}
          />

          <TimeSlotGrid label="Departure from Delhi" selected={filterDeparture} onChange={setFilterDeparture} />
          <TimeSlotGrid label="Arrival in Kanpur" selected={filterArrival} onChange={setFilterArrival} />

          <CheckboxFilter
            label="Train Types"
            options={trainTypeOptions}
            selected={filterTrainTypes}
            onChange={setFilterTrainTypes}
            counts={trainTypeCounts}
          />

          <div className="tp-filter-group">
            <h4 className="tp-filter-label">
              Max Duration <span className="tp-range-val">{Math.floor(filterMaxDuration / 60)}h {filterMaxDuration % 60}m</span>
            </h4>
            <input
              type="range" min={240} max={480} step={15}
              value={filterMaxDuration}
              onChange={(e) => setFilterMaxDuration(Number(e.target.value))}
              className="tp-slider"
            />
            <div className="tp-range-labels"><span>4h</span><span>8h</span></div>
          </div>

          <div className="tp-filter-group">
            <h4 className="tp-filter-label">
              Max Price <span className="tp-range-val">₹{filterMaxPrice.toLocaleString()}</span>
            </h4>
            <input
              type="range" min={100} max={3000} step={50}
              value={filterMaxPrice}
              onChange={(e) => setFilterMaxPrice(Number(e.target.value))}
              className="tp-slider"
            />
            <div className="tp-range-labels"><span>₹100</span><span>₹3,000</span></div>
          </div>
        </aside>

        {/* ── Main Results ── */}
        <main className="tp-main">
          <div className="tp-results-header">
            <div className="tp-results-meta">
              <button className="tp-filter-toggle" onClick={() => setFiltersOpen(!filtersOpen)}>
                {filtersOpen ? "◀ Hide" : "▶ Filters"}
                {activeFilterCount > 0 && <span className="tp-filter-badge sm">{activeFilterCount}</span>}
              </button>
              <div className="tp-results-info">
                <span className="tp-count-big">{filtered.length}</span>
                <span className="tp-count-label"> trains found</span>
                <span className="tp-route-pill">{from} → {to}</span>
              </div>
            </div>
            <div className="tp-sort-bar">
              <span className="tp-sort-label">Sort:</span>
              {sortOptions.map((s) => (
                <button
                  key={s.value}
                  className={`tp-sort-btn ${sortBy === s.value ? "tp-sort-active" : ""}`}
                  onClick={() => setSortBy(s.value)}
                >{s.label}</button>
              ))}
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="tp-no-results">
              <span>🚂</span>
              <h3>No trains found</h3>
              <p>Try adjusting your filters</p>
              <button className="tp-clear-btn big" onClick={clearAll}>Clear Filters</button>
            </div>
          ) : (
            <div className="tp-train-list">
              {filtered.map((train, idx) => (
                <TrainCard
                  key={train.id}
                  train={train}
                  idx={idx}
                  selectedDate={selectedDate}
                  setSelectedDate={setSelectedDate}
                  expandedId={expandedId}
                  setExpandedId={setExpandedId}
                />
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
